import { useState, useMemo } from 'react';
import axios from 'axios';
import { handleError, formatCurrency } from '../utils/lib';
import PaymentDialog from '../components/PaymentDialog';
import { useTranslation } from 'react-i18next';

const usePaymentDialog = (onSuccess, showSnackbar) => {
  const { t } = useTranslation();
  // Estado para los tipos de pago
  const [paymentTypes, setPaymentTypes] = useState([]);
  
  // Estado para el diálogo de agregar pago
  const [addPaymentDialog, setAddPaymentDialog] = useState({
    open: false,
    owner: null,
    amount: '',
    paymentType: null,
    isLoading: false
  });

  // Abrir diálogo para agregar pago
  const handleAddPayment = async (owner) => {
    try {
      // Fetch payment types only when needed
      let currentPaymentTypes = paymentTypes;
      if (paymentTypes.length === 0) {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}finance?payment_types`,
          { withCredentials: true }
        );
        currentPaymentTypes = response.data.data;
        setPaymentTypes(currentPaymentTypes);
      }
      
      // Find the "Efectivo" payment type to set as default
      const cashPaymentType = currentPaymentTypes.find(type => type.name.toLowerCase() === 'efectivo') || null;
      
      setAddPaymentDialog({
        open: true,
        owner: owner,
        amount: owner.balance?.toString() || '',
        paymentType: cashPaymentType,
        isLoading: false
      });
    } catch (error) {
      handleError(error, showSnackbar);
    }
  };

  // Cerrar diálogo de agregar pago
  const handleAddPaymentClose = () => {
    setAddPaymentDialog({
      open: false,
      owner: null,
      amount: '',
      paymentType: null,
      isLoading: false
    });
  };

  // Manejar cambio en el monto
  const handleAmountChange = (event) => {
    setAddPaymentDialog(prev => ({
      ...prev,
      amount: event.target.value
    }));
  };

  // Manejar cambio en el tipo de pago
  const handlePaymentTypeChange = (event, newValue) => {
    setAddPaymentDialog(prev => ({
      ...prev,
      paymentType: newValue
    }));
  };

  // Confirmar el pago
  const handleAddPaymentConfirm = async () => {
    try {
      if (!addPaymentDialog.paymentType) {
        showSnackbar(t('reports.pleaseSelectPaymentMethod'));
        return;
      }

      if (!addPaymentDialog.amount || parseFloat(addPaymentDialog.amount) <= 0) {
        showSnackbar(t('reports.pleaseEnterValidAmount'));
        return;
      }

      // Activar estado de loading
      setAddPaymentDialog(prev => ({
        ...prev,
        isLoading: true
      }));
      
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}sells_payments`,
        {
          owner: {
            id: addPaymentDialog.owner.id 
          },
          finance_payment_method: {
            id: addPaymentDialog.paymentType.id
          },
          amount: addPaymentDialog.amount,
        },
        { withCredentials: true }
      );
      
      if (response.data.status === 'SUCCESS') {
        // Cerrar diálogo
        handleAddPaymentClose();
        
        // Ejecutar callback de éxito (recargar datos)
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      // Desactivar loading en caso de error
      setAddPaymentDialog(prev => ({
        ...prev,
        isLoading: false
      }));
      handleError(error, showSnackbar);
    }
  };

  // Componente memoizado para evitar re-creaciones
  const paymentDialogElement = useMemo(() => (
    <PaymentDialog
      addPaymentDialog={addPaymentDialog}
      paymentTypes={paymentTypes}
      handleAddPaymentClose={handleAddPaymentClose}
      handleAddPaymentConfirm={handleAddPaymentConfirm}
      handleAmountChange={handleAmountChange}
      handlePaymentTypeChange={handlePaymentTypeChange}
    />
  ), [addPaymentDialog, paymentTypes]);

  // Datos y funciones que exponemos
  return {
    // Estados
    addPaymentDialog,
    paymentTypes,
    
    // Funciones para controlar el diálogo
    handleAddPayment,
    handleAddPaymentClose,
    handleAmountChange,
    handlePaymentTypeChange,
    handleAddPaymentConfirm,
    
    // Componente de diálogo renderizado
    PaymentDialog: paymentDialogElement
  };
};

export default usePaymentDialog; 